import { useState, useEffect, useCallback, useRef } from 'react';
import {
  CONTENT_HEIGHT_MM, FONT_SCALES, BASE_SCALE_INDEX, OPTIONAL_ORDER,
  DENSITIES, DEFAULT_COMPACT_SETTINGS, rowMm as rowMmFor, pxToMm, mmToPx,
} from '../config/compactCV';

/**
 * Mäter det kompakta dokumentet mot sidbudgeten.
 *
 * Personblocken tillåts brytas över sidgränsen medan enskilda projektrader
 * hålls ihop, vilket gör att innehållet flödar nästan kontinuerligt – därför
 * ligger scrollHeight mycket nära den tryckta höjden. Ett spill på en rad per
 * sidbrytning dras av som säkerhetsmarginal.
 */
export function useCompactFit(docRef, settings, deps = []) {
  const [measure, setMeasure] = useState({ heightMm: 0, wrappedRows: 0 });

  const run = useCallback(() => {
    const el = docRef.current;
    if (!el) return;

    // Cellerna är toppjusterade, så varje cells höjd är dess egen text.
    // Årskolumnen räknas inte – ett månadsintervall på två rader är avsett.
    const density = DENSITIES[settings.density] || DENSITIES.luftig;
    const linePx = mmToPx(rowMmFor(settings) - 2 * density.rowPadMm);
    let wrapped = 0;
    el.querySelectorAll('.cvc-row:not(.cvc-hrow)').forEach(row => {
      const isWrapped = Array.from(row.children).some(
        cell => !cell.classList.contains('cvc-yr') && cell.offsetHeight > linePx * 1.55,
      );
      row.setAttribute('data-wrapped', isWrapped ? '1' : '0');
      if (isWrapped) wrapped += 1;

      // Radbrytning kan bara mätas efter rendering, så förklaringen sätts
      // här i stället för i komponenten.
      const hints = [];
      if (isWrapped) {
        hints.push('Raden radbryter – en cell fick inte plats på en rad. '
          + 'Markeringen syns bara på skärmen, aldrig i utskriften.');
      }
      if (row.getAttribute('data-expandable') === '1') {
        hints.push('Klicka för att fälla ut beskrivning och bild.');
      }
      if (hints.length) row.title = hints.join(' ');
      else row.removeAttribute('title');
    });

    // Hur långt datumets andra rad hänger ned under rubrikraden i ett utfällt
    // uppdrag (se .cvc-yr i CompactCV). Bilden flyttas ned lika mycket.
    // Ett uppdrag som inte ryms på en sida får brytas, annars hålls det ihop.
    const pagePx = mmToPx(CONTENT_HEIGHT_MM);
    el.querySelectorAll('.cvc-assign').forEach(block => {
      const row = block.querySelector('.cvc-row');
      const parts = Array.from(block.querySelectorAll('.cvc-row > .cvc-yr .cvc-yrpart'));
      const over = block.getAttribute('data-open') === '1' && parts.length
        ? Math.max(0, ...parts.map(p => p.getBoundingClientRect().bottom))
          - row.getBoundingClientRect().bottom
        : 0;
      if (over > 0.5) block.style.setProperty('--yr-over', `${over.toFixed(1)}px`);
      else block.style.removeProperty('--yr-over');

      block.setAttribute('data-tall', block.offsetHeight > pagePx ? '1' : '0');
    });

    setMeasure({ heightMm: pxToMm(el.scrollHeight), wrappedRows: wrapped });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docRef, settings.fontScale, settings.density]);

  useEffect(() => {
    const id = requestAnimationFrame(run);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, settings, ...deps]);

  useEffect(() => {
    if (!docRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(run);
    ro.observe(docRef.current);
    return () => ro.disconnect();
  }, [docRef, run]);

  const row = rowMmFor(settings);
  const capacityMm = settings.pages * CONTENT_HEIGHT_MM - (settings.pages - 1) * row;
  const diffMm = measure.heightMm - capacityMm;

  return {
    heightMm: measure.heightMm,
    capacityMm,
    rowMm: row,
    wrappedRows: measure.wrappedRows,
    pagesUsed: measure.heightMm ? Math.max(1, Math.ceil(measure.heightMm / CONTENT_HEIGHT_MM)) : 0,
    overflowRows: diffMm > 0 ? Math.ceil(diffMm / row) : 0,
    freeRows: diffMm < 0 ? Math.floor(-diffMm / row) : 0,
    remeasure: run,
  };
}

/**
 * Nedskärningsstegen, i ordning:
 *   1. luftig täthet → tät
 *   2. övertecknad textstorlek ned mot 7,5 pt
 *   3. tillval bort, nedifrån och upp enligt prioritetsordningen
 *   4. textstorlek under 7,5 pt
 *   5. presentationen
 * Först när storleken redan är nere på grundvärdet offras innehåll.
 *
 * Bara innehåll som är på som standard och som inte kryssats i för hand får
 * kryssas bort. Projektinfo, mjukvaror och tillval som är av som standard
 * (t.ex. IT) är alltid ett aktivt val och tas aldrig bort i det tysta – får
 * dokumentet inte plats syns det i stället som rader för mycket.
 */
export function nextShrinkStep(settings) {
  const i = FONT_SCALES.findIndex(f => f.value === settings.fontScale);
  const scaleIndex = i === -1 ? BASE_SCALE_INDEX : i;
  const pinned = settings.pinned || [];
  const active = OPTIONAL_ORDER.filter(id => settings.optional.includes(id)
    && DEFAULT_COMPACT_SETTINGS.optional.includes(id) && !pinned.includes(id));
  const shrinkFont = () => ({
    ...settings,
    fontScale: FONT_SCALES[scaleIndex - 1].value,
    autoNote: `typsnitt ${FONT_SCALES[scaleIndex - 1].label} pt`,
  });

  if (settings.density !== 'tat') {
    return { ...settings, density: 'tat', autoNote: `täthet ${DENSITIES.tat.label.toLowerCase()}` };
  }
  if (scaleIndex > BASE_SCALE_INDEX) return shrinkFont();
  if (active.length) {
    const drop = active[active.length - 1];
    return {
      ...settings,
      optional: settings.optional.filter(id => id !== drop),
      autoNote: `tillval bortkryssat: ${drop}`,
    };
  }
  if (scaleIndex > 0) return shrinkFont();
  if (settings.showIngress && !pinned.includes('ingress')) {
    return { ...settings, showIngress: false, autoNote: 'presentation bortkryssad' };
  }
  return null; // Går inte att krympa mer – projekt måste kapas.
}

/** Kör nedskärningsstegen tills dokumentet får plats, max ett steg per pass. */
export function useAutoFit(fit, settings, setSettings) {
  // Nedskärningsstegen är högst nio till antalet, så räknaren kan aldrig snurra.
  const passes = useRef(0);

  useEffect(() => {
    if (!settings.autoFit || !fit.heightMm) return;
    if (fit.overflowRows <= 0) { passes.current = 0; return; }
    if (passes.current >= 10) return;

    const next = nextShrinkStep(settings);
    if (!next) return;
    passes.current += 1;
    setSettings(next);
  }, [fit.overflowRows, fit.heightMm, settings, setSettings]);
}
